import React, { useState, useEffect } from 'react';
import { X, Check, Crown, Zap, Star, Package, Gift, Sparkles } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useSubscription } from '../hooks/useSubscription';
import { getTranslatedSubscriptionPlans, formatPrice } from '../utils/subscriptionUtils';
import { useAuth } from '../hooks/useAuth';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUB_KEY);

interface PackagesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpgrade: (planId: string) => void;
  currentPlan: string;
  remainingAnalyses: number;
}

const PackagesModal: React.FC<PackagesModalProps> = (props) => {
  if (!props.isOpen) return null;
  
  const { t } = useTranslation();
  const [selectedPlan, setSelectedPlan] = useState<string>(props.currentPlan);
  const [isProcessing, setIsProcessing] = useState(false);
  const { session, user } = useAuth();
  const { refetch, loading } = useSubscription();

  const handlePurchase = async (planId: string, stripe_id: string) => {
    if (planId === 'free') return;
    setIsProcessing(true);

    try {
      // Store the attempted plan purchase in sessionStorage for cancellation tracking
      sessionStorage.setItem('attempted_plan_purchase', planId);
      
      await stripePromise;
      const res = await axios.post('https://jlyprvwmitisfutelyrj.supabase.co/functions/v1/checkout', {
        priceId: stripe_id,
        userId: user?.id,
        userEmail: user?.email,
        successUrl: window.location.origin + '/?payment_success=true&plan_id=' + planId,
        cancelUrl: window.location.origin + '/?payment_cancelled=true',
      }, {
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json'
        },
      });
      const data = await res.data;
      if (data.url) {
        window.location.href = data.url;
      } else {
        alert('Error redirecting to Stripe');
      }
    } catch (error) {
      alert('An error occurred. Please try again.');
      // Clear attempted plan on error
      sessionStorage.removeItem('attempted_plan_purchase');
    } finally {
      setIsProcessing(false);
    }
  };

  const getPlanIcon = (planId: string) => {
    switch (planId) {
      case 'free': return <Gift className="w-6 h-6" />;
      case 'decouverte': return <Star className="w-6 h-6" />;
      case 'avance': return <Zap className="w-6 h-6" />;
      case 'pro': return <Crown className="w-6 h-6" />;
      case 'welcome-offer': return <Sparkles className="w-6 h-6" />;
      default: return <Package className="w-6 h-6" />;
    }
  };

  const getPlanColor = (planId: string) => {
    switch (planId) {
      case 'free': return 'from-gray-500 to-gray-600';
      case 'decouverte': return 'from-green-500 to-green-600';
      case 'avance': return 'from-blue-500 to-blue-600';
      case 'pro': return 'from-purple-500 to-purple-600';
      case 'welcome-offer': return 'from-orange-500 to-red-500';
      default: return 'from-gray-500 to-gray-600';
    }
  };

  const getPlanName = (planId: string) => t(`subscription.plan.${planId}.name`);
  const getPlanDescription = (planId: string) => t(`subscription.plan.${planId}.desc`);

  // Combiner les plans avec l'offre de bienvenue si applicable
  const availablePlans = [...getTranslatedSubscriptionPlans(t)];

  useEffect(() => {
    if (loading) {
      refetch();
    }
  }, [loading, refetch]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        props.onClose();
      }
    };

    if (props.isOpen) {
      document.addEventListener('keydown', handleEscKey);
      return () => document.removeEventListener('keydown', handleEscKey);
    }
  }, [props]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{t('subscription.title')}</h2>
            <p className="text-gray-600 mt-1">{t('subscription.subtitle')}</p>
          </div>
          <button
            onClick={props.onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6`}>
            {availablePlans.map((plan) => {
              return (
                <div
                  key={plan.id}
                  className={`relative rounded-xl border-2 p-6 transition-all cursor-pointer ${
                    selectedPlan === plan.id
                      ? 'border-indigo-500 shadow-lg scale-105'
                      : 'border-gray-200 hover:border-gray-300'
                  } ${plan.popular ? 'ring-2 ring-blue-500 ring-opacity-50' : ''} ${
                    plan.id === 'welcome-offer' ? 'ring-2 ring-orange-500 ring-opacity-50 animate-pulse' : ''
                  }`}
                  onClick={() => setSelectedPlan(plan.id)}
                >
                  {plan.popular && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 w-36">
                      <span className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                        {t('subscription.popular')}
                      </span>
                    </div>
                  )}

                  {plan.id === 'welcome-offer' && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <span className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-3 py-1 rounded-full text-sm font-medium animate-pulse">
                        {t('subscription.welcomeOffer.badge')}
                      </span>
                    </div>
                  )}

                  <div className={`inline-flex p-3 rounded-lg bg-gradient-to-r ${getPlanColor(plan.id)} text-white mb-4`}>
                    {getPlanIcon(plan.id)}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{getPlanName(plan.id)}</h3>
                  
                  <div className="mb-4">
                    <span className="text-3xl font-bold text-gray-900">
                      {plan.price !== 0 && formatPrice(plan.price)}
                    </span>
                    {plan.id === 'welcome-offer' && (
                      <div className="text-sm text-gray-500 line-through">
                        {formatPrice(3.99)}
                      </div>
                    )}
                  </div>

                  <div className="mb-4">
                    <div className="text-lg font-semibold text-gray-900 mb-2">
                      {t('subscription.plan.analyses', { count: plan.analyses })}
                      {plan.id === 'free' ? t('subscription.plan.perMonthShort') : ''}
                    </div>
                    <p className="text-sm text-gray-600 mb-3">
                      {getPlanDescription(plan.id)}
                    </p>
                    {plan.price > 0 && (
                      <div className="text-sm text-gray-600">
                        {t('subscription.plan.perAnalysis', { price: formatPrice(plan.price / plan.analyses) })}
                      </div>
                    )}
                  </div>

                  <ul className="space-y-2 mb-6 text-sm">
                    {Array.isArray(plan.features) && plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <Check className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span className={`text-gray-700 ${feature.includes('🎁') ? 'font-bold text-orange-600' : ''}`}>
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => handlePurchase(plan.id, plan.stripe_id)}
                    disabled={isProcessing}
                    className={`w-full py-3 px-4 rounded-lg font-semibold transition-all ${
                      plan.id === 'free'
                        ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                        : selectedPlan === plan.id
                        ? `bg-gradient-to-r ${getPlanColor(plan.id)} text-white hover:shadow-lg transform hover:-translate-y-1 ${
                            plan.id === 'welcome-offer' ? 'animate-pulse' : ''
                          }`
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {isProcessing && selectedPlan === plan.id ? (
                      <div className="flex items-center justify-center">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        {t('subscription.processing')}
                      </div>
                    ) : plan.id === 'welcome-offer' ? (
                      t('subscription.takeOffer')
                    ) : (
                      t('subscription.buy', { planName: getPlanName(plan.id) })
                    )}
                  </button>
                </div>
              );
            })}
          </div>

          <div className="mt-8 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
            <div className="flex items-start">
              <div className="text-blue-500 mr-4 mt-1">
                <Package className="w-6 h-6" />
              </div>
              <div>
                <h4 className="font-semibold text-blue-900 mb-2">{t('subscription.why.title')}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-blue-800">
                  <ul className="space-y-1">
                    <li>{t('subscription.why.features1.0')}</li>
                    <li>{t('subscription.why.features1.1')}</li>
                    <li>{t('subscription.why.features1.2')}</li>
                    <li>{t('subscription.why.features1.3')}</li>
                  </ul>
                  <ul className="space-y-1">
                    <li>{t('subscription.why.features2.0')}</li>
                    <li>{t('subscription.why.features2.1')}</li>
                    <li>{t('subscription.why.features2.2')}</li>
                    <li>{t('subscription.why.features2.3')}</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 text-center">            
            {props.currentPlan === 'free' && props.remainingAnalyses > 0 && (
              <div className="bg-green-50 rounded-lg p-4 border border-green-200 mb-4">
                <div className="flex items-center justify-center mb-2">
                  <Gift className="w-5 h-5 text-green-600 mr-2" />
                  <span className="font-semibold text-green-800">{t('subscription.freeQuota.title')}</span>
                </div>
                <p className="text-sm text-green-700">
                  {t('subscription.freeQuota.desc')}
                </p>
              </div>
            )}
            
            <p className="text-sm text-gray-500">
              {t('subscription.footer')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PackagesModal;
